import { AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import type { RemovalOperatorMismatch } from '@shared/task-distribution/removalOperatorMismatch';

interface RemovalOperatorMismatchBannerProps {
  mismatches: RemovalOperatorMismatch[];
}

export function RemovalOperatorMismatchBanner({ mismatches }: RemovalOperatorMismatchBannerProps) {
  if (mismatches.length === 0) return null;

  const preview = mismatches.slice(0, 5);
  const remaining = mismatches.length - preview.length;
  const storeCount = new Set(mismatches.map((m) => m.storeId)).size;

  return (
    <Alert variant="destructive" className="border-red-300 bg-red-50 text-red-950">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>
        Format operator conflict ({mismatches.length} format{mismatches.length === 1 ? '' : 's'}
        {' across '}
        {storeCount} store{storeCount === 1 ? '' : 's'})
      </AlertTitle>
      <AlertDescription className="space-y-2">
        <p>
          Posting and removals for the <strong>same format</strong> at these stores are assigned to
          different operators. That format may not reach the right phone on mobile dispatch.
        </p>
        <ul className="list-disc pl-5 text-sm space-y-1">
          {preview.map((m) => (
            <li key={`${m.storeId}-${m.mediaFormatId}-${m.removalOperatorId}`}>
              <strong>{m.storeName}</strong>
              {' · '}
              {m.mediaFormatName}
              {' — posting: '}
              {m.postingOperatorName}
              {'; removals: '}
              {m.removalOperatorName}
            </li>
          ))}
        </ul>
        {remaining > 0 && (
          <p className="text-sm text-red-800">…and {remaining} more.</p>
        )}
      </AlertDescription>
    </Alert>
  );
}

export default RemovalOperatorMismatchBanner;
